import { Theme } from '@mui/system/createTheme';
import useMediaQuery from '@mui/system/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { ListItemSkeleton } from 'src/components/ListItem';
import { SearchTextField } from 'src/components/SearchTextField';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import {
  CartItem as CartItemType,
  PaymentOption,
} from 'src/models/cart/_types';
import { cartTypeToItemType } from 'src/models/cart/utils';
import { NoResults } from 'src/shared/NoResults';
import { ItemCard } from 'src/shared/SidebarDialog/ItemCard';
import { ItemType } from 'src/shared/constants/itemTypes';
import { Identifier } from 'src/types/utilityTypes';

interface SearchItem extends CartItemType {
  id: Identifier;
  name: string;
  aliases?: string[];
  header?: string;
  type: string;
  paymentOptions?: PaymentOption[];
}

interface SearchAndAddItemProps {
  onAdd: (item: SearchItem) => void;
  onRemove: (item: CartItemType) => void;
  itemList: CartItemType[];
  isLoading?: boolean;
  searchResults?: SearchItem[];
  activeFilters?: boolean;
  resetFilters: () => void;
  // New props to replace context dependencies
  selectedItemType?: ItemType | null;
  cartItems: CartItemType[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  checkIfItemAdded?: (item: SearchItem) => boolean;
}

export function SearchAndAddItem({
  onAdd,
  onRemove,
  itemList,
  activeFilters,
  isLoading = false,
  searchResults = [],
  resetFilters,
  selectedItemType,
  cartItems,
  searchValue,
  onSearchChange,
  checkIfItemAdded,
}: SearchAndAddItemProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'client.payment' });
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const items = Object.entries(
    searchResults.reduce((acc, item) => {
      if (!acc[item.type]) acc[item.type] = [];
      if (acc[item.type].length > 2 && !selectedItemType) return acc;
      acc[item.type].push(item);
      return acc;
    }, {} as Record<string, SearchItem[]>)
  );

  const isItemAdded =
    checkIfItemAdded ||
    ((item) => {
      return !!cartItems.find((i) => {
        return (
          i.itemId === item.id &&
          cartTypeToItemType(i.itemType) === item.type &&
          item.type !== 'package'
        );
      });
    });

  const showInitialMessage = !selectedItemType && !searchValue && !isLoading;

  return (
    <Stack sx={{ height: '100%' }} spacing={2}>
      {mdDown && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <SearchTextField
            value={searchValue}
            onCancel={() => {
              onSearchChange('');
            }}
            onSearch={onSearchChange}
            autoFocus={itemList.length === 0}
          />
        </Stack>
      )}
      <Box>
        {isLoading && (
          <Box>
            <ListItemSkeleton sx={{ px: 0 }} avatar primary />
            <ListItemSkeleton sx={{ px: 0 }} avatar primary />
            <ListItemSkeleton sx={{ px: 0 }} avatar primary />
            <ListItemSkeleton sx={{ px: 0 }} avatar primary />
            <ListItemSkeleton sx={{ px: 0 }} avatar primary />
          </Box>
        )}
        {showInitialMessage && (
          <Box
            sx={{
              height: '50svh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography color="textSecondary" align="center" sx={{ p: 2 }}>
              {t('addItems')}
            </Typography>
          </Box>
        )}
        {!showInitialMessage && (
          <>
            {!activeFilters && !searchResults.length && !isLoading && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 4,
                }}
              >
                <NoResults compact title={t('noItemsFound')}></NoResults>
              </Box>
            )}
            {!!searchResults.length &&
              items.map(([type, items]) => (
                <Box key={type}>
                  {!selectedItemType && (
                    <Typography
                      variant="subtitle2"
                      sx={{
                        px: 2,
                        py: 1,
                        my: 2,
                        bgcolor: 'grey.100',
                        position: 'sticky',
                        borderRadius: 1,
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </Typography>
                  )}
                  <Stack spacing={2}>
                    {items.map((item) => (
                      <ItemCard
                        key={`${item.id}-${item.type}`}
                        item={item}
                        itemAdded={isItemAdded(item)}
                        showTypeLabel={!selectedItemType}
                        onAdd={onAdd}
                        onRemove={onRemove}
                      />
                    ))}
                  </Stack>
                </Box>
              ))}

            {activeFilters && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 4,
                }}
              >
                <NoResults compact title={t('cannotFindItems')}>
                  <Button onClick={resetFilters}>{t('clearFilters')}</Button>
                </NoResults>
              </Box>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
}
